/**
 * SA Cart Sync
 * Synchronizes cart items with the same _group_id.
 * - If parent is removed, remove children.
 * - If parent quantity changes, update children quantity.
 * - Disables quantity inputs for children.
 * - Intercepts cart API calls to bundle parent+child updates.
 */

(function() {
  const selectors = {
    cartItem: '.m-cart-item',
    property: '.m-cart-item__property',
    propertyName: '[data-cart-item-property-name]',
    propertyValue: '[data-cart-item-property-value]',
    quantityInput: 'input[name="updates[]"]',
    quantityButtons: 'button[name="plus"], button[name="minus"]'
  };

  const GROUP_ID_KEY = '_group_id';
  const TYPE_KEY = '_type';
  const CHILD_TYPE = 'customize_frame'; 
  const PARENT_TYPE = 'parent';

  // --- Helpers ---
  function getCart() {
    return fetch(window.Shopify.routes.root + 'cart.js')
      .then(res => res.json());
  }

  function updateCart(updates) {
    return fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    }).then(res => res.json());
  }
  
  function debounce(func, wait) {
    let timeout;
    return function(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  // --- UI Logic ---
  function getProperties(itemElement) {
    const props = {};
    const propEls = itemElement.querySelectorAll(selectors.property);
    propEls.forEach(el => {
      const nameEl = el.querySelector(selectors.propertyName);
      const valueEl = el.querySelector(selectors.propertyValue);
      if (nameEl && valueEl) {
        const name = nameEl.textContent.trim().replace(/:$/, '');
        const value = valueEl.textContent.trim();
        props[name] = value;
      }
    });
    return props;
  }

  function getLineIndex(itemElement) {
    if (itemElement.dataset.index) {
        return parseInt(itemElement.dataset.index);
    }
    return null;
  }

  function getKey(itemElement) {
    return itemElement.dataset.key || null;
  }

  function getQuantity(itemElement) {
    const input = itemElement.querySelector(selectors.quantityInput);
    if (input) {
        return parseInt(input.value);
    }
    return null;
  }

  function disableChildQuantityInputs() {
    const items = document.querySelectorAll(selectors.cartItem);
    items.forEach(item => {
      const props = getProperties(item);
      if (props[TYPE_KEY] === CHILD_TYPE) {
        const input = item.querySelector(selectors.quantityInput);
        const buttons = item.querySelectorAll(selectors.quantityButtons);
        
        if (input) {
           input.disabled = true;
           input.setAttribute('readonly', true);
           input.style.opacity = '0.5';
           input.style.pointerEvents = 'none';
        }
        buttons.forEach(btn => {
            btn.disabled = true;
            btn.style.opacity = '0.5';
            btn.style.pointerEvents = 'none';
        });
      }
    });
  }

  // --- Event Interception (Replace Theme Logic for Parents) ---
  function initEventInterception() {
    // Capture phase listeners to preempt theme logic
    document.addEventListener('change', handleInterceptedChange, true);
    document.addEventListener('click', handleInterceptedClick, true);
  }

  function handleInterceptedChange(e) {
    if (e.target.matches(selectors.quantityInput)) {
        const itemElement = e.target.closest(selectors.cartItem);
        if (isParentItem(itemElement)) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            
            const newQty = parseInt(e.target.value);
            handleUnifiedUpdate(itemElement, newQty);
        }
    }
  }

  function handleInterceptedClick(e) {
    // Handle Remove Button
    const removeBtn = e.target.closest('m-cart-remove-button, .m-cart-item__remove'); // Adjust selector as needed
    if (removeBtn) {
        const itemElement = removeBtn.closest(selectors.cartItem);
        if (isParentItem(itemElement)) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            
            handleUnifiedUpdate(itemElement, 0);
            return;
        }
    }

    // Handle Plus/Minus Buttons
    const qtyBtn = e.target.closest(selectors.quantityButtons);
    if (qtyBtn) {
        const itemElement = qtyBtn.closest(selectors.cartItem);
        if (isParentItem(itemElement)) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();

            const input = itemElement.querySelector(selectors.quantityInput);
            if (input) {
                let currentQty = parseInt(input.value);
                const isPlus = qtyBtn.name === 'plus' || qtyBtn.matches('[name="plus"]');
                const newQty = isPlus ? currentQty + 1 : currentQty - 1;
                
                if (newQty >= 0) {
                    input.value = newQty; // Optimistic update for Parent
                    handleUnifiedUpdate(itemElement, newQty);
                }
            }
        }
    }
  }

  function isParentItem(itemElement) {
    if (!itemElement) return false;
    const props = getProperties(itemElement);
    return props[TYPE_KEY] === PARENT_TYPE && props[GROUP_ID_KEY];
  }

  async function handleUnifiedUpdate(parentItem, newQty) {
    const props = getProperties(parentItem);
    const groupId = props[GROUP_ID_KEY];
    const parentKey = getKey(parentItem);

    // 1. Optimistic UI Update for Children
    const allItems = document.querySelectorAll(selectors.cartItem);
    const childrenKeys = [];
    
    allItems.forEach(item => {
        const p = getProperties(item);
        if (p[GROUP_ID_KEY] === groupId && p[TYPE_KEY] === CHILD_TYPE) {
            const k = getKey(item);
            if (k) childrenKeys.push(k);
            
            // Optimistic DOM update
            const childInput = item.querySelector(selectors.quantityInput);
            if (childInput) childInput.value = newQty;
            
            // Visual feedback (optional: opacity for remove)
            if (newQty === 0) {
                item.style.opacity = '0.5';
                parentItem.style.opacity = '0.5';
            }
        }
    });

    // 2. Prepare Payload
    const updates = {};
    if (parentKey) updates[parentKey] = newQty;
    childrenKeys.forEach(k => updates[k] = newQty);

    // 3. Send Request
    try {
        await updateCart(updates);
        // 4. Force Theme Refresh
        refreshCartUI();
    } catch (err) {
        console.error('SA Sync: Update failed', err);
        // Revert UI if needed (optional, but complexity increases)
        refreshCartUI(); // Refreshing will reset to server state anyway
    }
  }

  // --- Sync Logic (Fallback & Safety) ---
  // We keep this but rely on Interceptor for the main action.
  async function checkAndSyncCart() {
     // ... (Keep existing logic but it will likely find nothing to do if interceptor works)
     // For safety, we keep the logic to fix any desync that might happen via other means.
     // Code condensed for brevity as we focus on Interceptor.
     try {
       const updates = {};
       let hasChanges = false;
       let needsFetch = false;
       const groups = {};

       const domItems = document.querySelectorAll(selectors.cartItem);
       if (domItems.length > 0) {
           domItems.forEach(item => {
               const props = getProperties(item);
               const qty = getQuantity(item);
               const lineIndex = getLineIndex(item);
               const key = getKey(item);
               if (props[GROUP_ID_KEY]) {
                   const groupId = props[GROUP_ID_KEY];
                   if (!groups[groupId]) groups[groupId] = { parent: null, children: [] };
                   const itemData = { line: lineIndex, key: key, quantity: qty, properties: props };
                   if (props[TYPE_KEY] === PARENT_TYPE) groups[groupId].parent = itemData;
                   else if (props[TYPE_KEY] === CHILD_TYPE) groups[groupId].children.push(itemData);
               }
           });
           Object.keys(groups).forEach(groupId => {
               const group = groups[groupId];
               if (!group.parent && group.children.length > 0) needsFetch = true;
               else if (group.parent && group.children.length > 0) {
                   const targetQty = group.parent.quantity;
                   group.children.forEach(child => {
                       if (child.quantity !== null && child.quantity !== targetQty) {
                           if (child.key) { updates[child.key] = targetQty; hasChanges = true; }
                           else if (child.line) { updates[child.line] = targetQty; hasChanges = true; }
                           else needsFetch = true;
                       }
                   });
               }
           });
       } else needsFetch = true;

       if (needsFetch || (!hasChanges && domItems.length === 0)) {
           const cart = await getCart();
           const items = cart.items;
           const apiGroups = {};
           items.forEach(item => {
             if (item.properties && item.properties[GROUP_ID_KEY]) {
               const groupId = item.properties[GROUP_ID_KEY];
               if (!apiGroups[groupId]) apiGroups[groupId] = { parent: null, children: [] };
               if (item.properties[TYPE_KEY] === PARENT_TYPE) apiGroups[groupId].parent = item;
               else if (item.properties[TYPE_KEY] === CHILD_TYPE) apiGroups[groupId].children.push(item);
             }
           });
           Object.keys(apiGroups).forEach(groupId => {
             const group = apiGroups[groupId];
             if (!group.parent && group.children.length > 0) {
               group.children.forEach(child => { updates[child.key] = 0; hasChanges = true; });
             } else if (group.parent && group.children.length > 0) {
               const targetQty = group.parent.quantity;
               group.children.forEach(child => {
                 if (child.quantity !== targetQty) { updates[child.key] = targetQty; hasChanges = true; }
               });
             }
           });
       }

       if (hasChanges) {
         await updateCart(updates);
         refreshCartUI();
       }
     } catch (e) {
       console.error('SA Sync: Error syncing cart:', e);
     }
  }

  function refreshCartUI() {
    const cartDrawer = document.querySelector('m-cart-drawer');
    if (cartDrawer && typeof cartDrawer.onCartDrawerUpdate === 'function') {
        cartDrawer.onCartDrawerUpdate();
    }
    const mainCart = document.querySelector('m-cart');
    if (mainCart && typeof mainCart.onCartUpdate === 'function') {
        mainCart.onCartUpdate();
    }
    if (window.MinimogEvents && window.MinimogTheme) {
        window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, { source: 'sa-cart-sync' });
    } else if (window.location.pathname.includes('/cart')) {
        window.location.reload();
    }
  }

  // --- Initialization ---
  
  const observer = new MutationObserver((mutations) => {
    let shouldCheck = false;
    mutations.forEach(m => {
      if (m.addedNodes.length > 0) {
        shouldCheck = true;
      }
    });
    
    if (shouldCheck) {
      disableChildQuantityInputs();
    }
  });

  function initObserver() {
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function initSyncListener() {
    const debouncedSync = debounce(checkAndSyncCart, 50);

    if (window.MinimogEvents && window.MinimogTheme) {
      window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.cartUpdate, (event) => {
        if (event && event.source === 'sa-cart-sync') return;
        debouncedSync();
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      // initFetchInterceptor(); // Removed
      initEventInterception();
      initObserver();
      initSyncListener();
      // checkAndSyncCart(); // Removed fallback to avoid conflicts
      disableChildQuantityInputs();
    });
  } else {
    // initFetchInterceptor(); // Removed
    initEventInterception();
    initObserver();
    initSyncListener();
    // checkAndSyncCart(); // Removed fallback to avoid conflicts
    disableChildQuantityInputs();
  }

})();
